import React, { useState, useEffect } from 'react';
import { taskService } from '../services/taskService';
import { FaPlus, FaCheck, FaTrash, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

function Tweek() {
  const [tasks, setTasks] = useState([]);
  const [dayInputs, setDayInputs] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [anydayTasks, setAnydayTasks] = useState([]);
  const [newAnydayTask, setNewAnydayTask] = useState('');
  const [weekOffset, setWeekOffset] = useState(0);

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await taskService.getTasks();
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const getWeekDays = () => {
    const days = [];
    const current = new Date();
    current.setDate(current.getDate() - current.getDay() + (weekOffset * 7));

    for (let i = 0; i < 7; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    return days;
  };

  const navigateWeek = (direction) => {
    setWeekOffset(prev => prev + direction);
  };

  const handleInputChange = (day, value) => {
    const dayStr = day.toISOString().split('T')[0];
    setDayInputs(prev => ({
      ...prev,
      [dayStr]: value
    }));
  };

  const handleAddTask = async (day) => {
    const dayStr = day.toISOString().split('T')[0];
    const inputValue = dayInputs[dayStr];
    
    if (!inputValue?.trim()) return;

    try {
      const response = await taskService.createTask({
        title: inputValue,
        date: dayStr,
        completed: false
      });
      setTasks([...tasks, response.data]);
      // Clear only this day's input
      setDayInputs(prev => ({
        ...prev,
        [dayStr]: ''
      }));
    } catch (error) {
      console.error('Error adding task:', error);
    }
  };

  const handleAddAnydayTask = async () => {
    if (!newAnydayTask.trim()) return;

    try {
      const response = await taskService.createTask({
        title: newAnydayTask,
        date: null, // null date indicates it's an "anyday" task
        completed: false,
        isAnyday: true
      });
      setAnydayTasks([...anydayTasks, response.data]);
      setNewAnydayTask('');
    } catch (error) {
      console.error('Error adding anyday task:', error);
    }
  };

  const handleToggleTask = async (taskId) => {
    const task = tasks.find(t => t._id === taskId);
    try {
      await taskService.updateTask(taskId, { ...task, completed: !task.completed });
      setTasks(tasks.map(t => 
        t._id === taskId ? { ...t, completed: !t.completed } : t
      ));
    } catch (error) {
      console.error('Error updating task:', error);
    }
  };

  const handleDeleteTask = async (taskId) => {
    try {
      await taskService.deleteTask(taskId);
      setTasks(tasks.filter(t => t._id !== taskId));
    } catch (error) {
      console.error('Error deleting task:', error);
    }
  };

  const getTasksForDate = (date) => {
    return tasks.filter(task => 
      new Date(task.date).toDateString() === date.toDateString()
    );
  };

  const getMonthYearDisplay = () => {
    const firstDay = getWeekDays()[0];
    const lastDay = getWeekDays()[6];
    
    const firstMonth = firstDay.toLocaleString('default', { month: 'long' });
    const lastMonth = lastDay.toLocaleString('default', { month: 'long' });
    const firstYear = firstDay.getFullYear();
    const lastYear = lastDay.getFullYear();
    
    if (firstMonth === lastMonth && firstYear === lastYear) {
      return `${firstMonth} ${firstYear}`;
    } else if (firstYear === lastYear) {
      return `${firstMonth} - ${lastMonth} ${firstYear}`;
    }
    return `${firstMonth} ${firstYear} - ${lastMonth} ${lastYear}`;
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      <div className="mb-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Weekly Tasks</h1>
          <h2 className="text-lg text-gray-600 mt-1">{getMonthYearDisplay()}</h2>
        </div>
        <div className="flex gap-4 items-center">
          <button
            onClick={() => navigateWeek(-1)}
            className="p-2 rounded-lg hover:bg-gray-100"
            title="Previous Week"
          >
            <FaChevronLeft className="w-5 h-5 text-gray-600" />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className="px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
          >
            Current Week
          </button>
          <button
            onClick={() => navigateWeek(1)}
            className="p-2 rounded-lg hover:bg-gray-100"
            title="Next Week"
          >
            <FaChevronRight className="w-5 h-5 text-gray-600" />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-4">
        {getWeekDays().map((day, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-4 flex flex-col h-[calc(100vh-12rem)]">
            <div className="text-sm font-semibold text-gray-600 mb-2">
              {day.toLocaleDateString('en-US', { weekday: 'short' })}
            </div>
            <div className="text-lg font-bold mb-4">
              {day.getDate()}
            </div>

            <div className="flex-grow">
              {getTasksForDate(day).map(task => (
                <div key={task._id} 
                     className="flex items-center justify-between p-2 hover:bg-gray-50 rounded">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleTask(task._id)}
                      className={`w-4 h-4 border rounded ${
                        task.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                      }`}
                    >
                      {task.completed && <FaCheck className="text-white text-xs" />}
                    </button>
                    <span className={task.completed ? 'line-through text-gray-400' : ''}>
                      {task.title}
                    </span>
                  </div>
                  <button
                    onClick={() => handleDeleteTask(task._id)}
                    className="text-red-500 hover:text-red-600"
                  >
                    <FaTrash className="text-xs" />
                  </button>
                </div>
              ))}
            </div>

            <div className="mt-auto pt-4 border-t">
              <div className="flex gap-2">
                <input
                  type="text"
                  placeholder="Add task..."
                  value={dayInputs[day.toISOString().split('T')[0]] || ''}
                  onChange={(e) => handleInputChange(day, e.target.value)}
                  className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddTask(day);
                    }
                  }}
                />
                <button
                  onClick={() => handleAddTask(day)}
                  className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                >
                  <FaPlus />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Anyday Section */}
      <div className="bg-gray-50 rounded-lg shadow-md p-6 mt-8">
        <h2 className="text-xl font-bold text-gray-800 mb-4">Anyday Tasks</h2>
        
        <div className="space-y-2 max-h-[300px] overflow-y-auto mb-4">
          {anydayTasks.map(task => (
            <div key={task._id} 
                 className="flex items-center justify-between p-3 bg-white rounded-lg shadow-sm">
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleToggleTask(task._id)}
                  className={`w-4 h-4 border rounded ${
                    task.completed ? 'bg-green-500 border-green-500' : 'border-gray-300'
                  }`}
                >
                  {task.completed && <FaCheck className="text-white text-xs" />}
                </button>
                <span className={task.completed ? 'line-through text-gray-400' : ''}>
                  {task.title}
                </span>
              </div>
              <button
                onClick={() => handleDeleteTask(task._id)}
                className="text-red-500 hover:text-red-600"
              >
                <FaTrash className="text-xs" />
              </button>
            </div>
          ))}
        </div>

        <div className="flex gap-2">
          <input
            type="text"
            placeholder="Add anyday task..."
            value={newAnydayTask}
            onChange={(e) => setNewAnydayTask(e.target.value)}
            className="w-full p-2 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddAnydayTask();
              }
            }}
          />
          <button
            onClick={handleAddAnydayTask}
            className="p-2 text-blue-600 hover:bg-blue-50 rounded"
          >
            <FaPlus />
          </button>
        </div>
      </div>
    </div>
  );
}

export default Tweek;
